import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '@resource-kit/ui';
import {
  TAB_LIST_CLASS,
  TAB_TRIGGER_ACTIVE_CLASS,
  TAB_TRIGGER_CLASS,
  TAB_TRIGGER_IDLE_CLASS,
} from '@resource-kit/ui/react';

/**
 * A tab strip whose tabs are ROUTES (#2005).
 *
 * The route strips (the merged sidebar entries' strips of `nav-groups.ts`, #2476;
 * before that the `/audit` and `/webhooks` strips) once each hand-rolled their
 * own underline markup, and each drifted a little from `ui/Tabs` (the in-page
 * strip the operations inbox uses): different padding, different gap. Surfaces
 * that are meant to read as the same control had different appearances.
 *
 * The look comes from `ui/Tabs`'s own exported class constants (list, trigger,
 * active and idle states), so the two cannot drift again. What is
 * NOT shared is the widget: `Tabs` is a controlled base-ui primitive whose tabs
 * are values, and these are LINKS. Wrapping it to emit anchors would mean
 * fighting its roving-focus and value semantics to win back what a link already
 * does — middle-click, preview, open in a new tab.
 *
 * `NavLink` stamps `aria-current="page"` on the active route, so the highlight
 * and the assistive-tech state cannot disagree.
 */

export interface RouteTab {
  /** Destination. Compared with `end` so a parent path does not stay lit. */
  to: string;
  /** Already-translated label — the caller owns i18n. */
  label: ReactNode;
}

export interface RouteTabsProps {
  tabs: readonly RouteTab[];
  /** Accessible name for the strip, e.g. "Audit views". */
  ariaLabel: string;
  className?: string;
}

export function RouteTabs({ tabs, ariaLabel, className }: RouteTabsProps) {
  return (
    // One box: the segmented track needs no baseline to sit on. The strip
    // scrolls sideways and must never wrap; `ui/Tabs` documents that rule, and
    // both halves of it ride in the shared classes.
    <nav aria-label={ariaLabel} className={cn(TAB_LIST_CLASS, className)}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end
          className={({ isActive }) =>
            cn(TAB_TRIGGER_CLASS, isActive ? TAB_TRIGGER_ACTIVE_CLASS : TAB_TRIGGER_IDLE_CLASS)
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default RouteTabs;
